/**
 * Analytics Integration for MTHK Shopify Store
 * Handles: Google Tag Manager, Facebook Pixel, Klaviyo
 *
 * Integrates with Shopify's Customer Events (Web Pixels) when available,
 * falls back to direct implementation when not.
 *
 * Configuration is managed via theme settings or can be hardcoded below
 */

// Configuration - Update these or pull from theme settings
const CONFIG = {
  GTM_ID: 'GTM-W5W2D8P',
  FB_PIXEL_ID: '996117464580016',
  KLAVIYO_ID: 'VYrTez',
  DEFAULT_CURRENCY: 'GBP',
  DEFAULT_PRODUCT_BRAND: 'MTHK',
  DEFAULT_PRODUCT_CATEGORY: 'MTHK Product',
  // Set to true to use Shopify's tracking instead of direct pixel/GTM initialization
  USE_SHOPIFY_TRACKING: true,
}

class Analytics {
  constructor() {
    this.isKlaviyoLoaded = false
    this.isGTMLoaded = false
    this.isFBPixelLoaded = false
    this.useShopifyTracking = CONFIG.USE_SHOPIFY_TRACKING && this.hasShopifyAnalytics()
  }

  /**
   * Check if Shopify's analytics.publish is available
   */
  hasShopifyAnalytics() {
    return typeof window !== 'undefined' &&
           window.Shopify &&
           window.Shopify.analytics &&
           typeof window.Shopify.analytics.publish === 'function'
  }

  /**
   * Initialize all analytics platforms
   */
  init() {
    // Only initialize on client side
    if (typeof window === 'undefined') return

    if (this.useShopifyTracking) {
      console.log('[Analytics] Using Shopify Customer Events API')
      // Still initialize Klaviyo directly as it's not part of Shopify's system
      this.initKlaviyo()
    } else {
      console.log('[Analytics] Using direct pixel/GTM integration')
      this.initGTM()
      this.initFacebookPixel()
      this.initKlaviyo()
    }

    this.setupEventListeners()

    console.log('[Analytics] Initialized')
  }

  /**
   * Initialize Google Tag Manager
   */
  initGTM() {
    if (window.dataLayer) {
      this.isGTMLoaded = true
      return
    }

    window.dataLayer = window.dataLayer || []

    // GTM Script
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${CONFIG.GTM_ID}`

    script.onload = () => {
      this.isGTMLoaded = true
      window.dispatchEvent(new Event('gtm-loaded'))
      console.log('[Analytics] GTM loaded')
    }

    document.head.appendChild(script)

    // Initialize dataLayer
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    })
  }

  /**
   * Initialize Facebook Pixel
   */
  initFacebookPixel() {
    /* eslint-disable */
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    window.fbq('init', CONFIG.FB_PIXEL_ID)
    this.isFBPixelLoaded = true

    // Disable push state to prevent double tracking on navigation
    window.fbq.disablePushState = true

    console.log('[Analytics] Facebook Pixel loaded')
  }

  /**
   * Initialize Klaviyo
   */
  initKlaviyo() {
    const script = document.createElement('script')
    script.src = `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${CONFIG.KLAVIYO_ID}`
    script.async = true
    script.defer = true

    script.addEventListener('load', () => {
      this.isKlaviyoLoaded = true
      window.dispatchEvent(new Event('klaviyo-loaded'))
      console.log('[Analytics] Klaviyo loaded')
    })

    document.body.appendChild(script)
  }

  /**
   * Setup event listeners for Shopify events
   */
  setupEventListeners() {
    // Track initial page view
    this.trackPageView()

    // Listen for Shopify theme navigation events (if using sections)
    document.addEventListener('shopify:section:load', () => {
      this.trackPageView()
    })

    // Listen for the theme's cart:update event (dispatched by theme on add to cart)
    document.addEventListener('cart:update', (event) => {
      if (event.detail && event.detail.data && !event.detail.data.didError) {
        this.handleThemeCartAdd(event.detail)
      }
    })

    // Listen for custom add to cart events (for backwards compatibility)
    document.addEventListener('product:added-to-cart', (event) => {
      if (event.detail) {
        this.trackAddToCart(event.detail)
      }
    })
  }

  /**
   * Push data to GTM dataLayer
   */
  pushToDataLayer(data) {
    if (!window.dataLayer) {
      console.warn('[Analytics] GTM not loaded yet')
      return
    }
    window.dataLayer.push(data)
  }

  /**
   * Push event to Klaviyo
   */
  pushToKlaviyo(eventName, data) {
    if (!this.isKlaviyoLoaded) {
      console.log('[Analytics] Klaviyo not loaded. Queuing event:', eventName)
      window.addEventListener('klaviyo-loaded', () => {
        window._learnq.push(['track', eventName, data])
      }, { once: true })
      return
    }

    window._learnq.push(['track', eventName, data])
    console.log('[Analytics] Klaviyo event:', eventName, data)
  }

  /**
   * Track page view across all platforms
   */
  trackPageView() {
    if (this.useShopifyTracking) {
      // Use Shopify's Customer Events API
      window.Shopify.analytics.publish('page_viewed', {
        path: window.location.pathname,
        title: document.title,
        url: window.location.href
      })
    } else {
      // Facebook Pixel
      if (this.isFBPixelLoaded) {
        window.fbq('track', 'PageView')
      }

      // GTM
      this.pushToDataLayer({
        event: 'page_view',
        page_path: window.location.pathname,
        page_title: document.title
      })
    }

    console.log('[Analytics] Page view tracked')
  }

  /**
   * Track product view
   */
  trackProductView(product) {
    if (this.useShopifyTracking) {
      // Use Shopify's Customer Events API for product views
      window.Shopify.analytics.publish('product_viewed', {
        productVariant: {
          id: product.variants?.[0]?.id || product.id,
          price: {
            amount: product.price / 100,
            currencyCode: CONFIG.DEFAULT_CURRENCY
          },
          sku: product.variants?.[0]?.sku || '',
          title: product.title,
          product: {
            id: product.id,
            title: product.title,
            vendor: product.vendor || CONFIG.DEFAULT_PRODUCT_BRAND,
            type: product.type,
            url: window.location.pathname
          }
        }
      })
    } else {
      // Facebook Pixel
      if (this.isFBPixelLoaded) {
        window.fbq('track', 'ViewContent', {
          content_name: product.title,
          content_ids: [product.id],
          content_type: 'product',
          value: product.price / 100,
          currency: CONFIG.DEFAULT_CURRENCY
        })
      }

      // GTM Enhanced Ecommerce
      this.pushToDataLayer({
        event: 'view_item',
        ecommerce: {
          currency: CONFIG.DEFAULT_CURRENCY,
          value: product.price / 100,
          items: [{
            item_id: product.variants?.[0]?.sku || product.id,
            item_name: product.title,
            item_brand: CONFIG.DEFAULT_PRODUCT_BRAND,
            item_category: product.type || CONFIG.DEFAULT_PRODUCT_CATEGORY,
            price: product.price / 100,
            quantity: 1
          }]
        }
      })
    }

    // Klaviyo (always track regardless of Shopify integration)
    this.pushToKlaviyo('Viewed Product', {
      ProductName: product.title,
      ProductID: product.id,
      SKU: product.variants?.[0]?.sku || '',
      Categories: product.tags || [],
      ImageURL: product.featured_image,
      URL: window.location.href,
      Brand: CONFIG.DEFAULT_PRODUCT_BRAND,
      Price: product.price / 100,
      CompareAtPrice: product.compare_at_price ? product.compare_at_price / 100 : null
    })

    console.log('[Analytics] Product view tracked:', product.title)
  }

  /**
   * Handle theme's cart:update event
   * Converts theme event data to our analytics format
   */
  handleThemeCartAdd(eventDetail) {
    const { data } = eventDetail

    // Get product data from window.productData (set on product pages)
    if (!window.productData) {
      console.warn('[Analytics] Product data not available for cart tracking')
      return
    }

    const product = window.productData

    // Find the variant that was added
    const variantId = data.variantId
    const variant = product.variants?.find(v => v.id.toString() === variantId?.toString())

    if (!variant) {
      console.warn('[Analytics] Variant not found for cart tracking')
      return
    }

    // Convert to our expected format and track
    // Use product title if variant title is "Default Title" (single variant products)
    const variantTitle = variant.title === 'Default Title' ? product.title : variant.title

    this.trackAddToCart({
      product: {
        id: product.id,
        title: product.title,
        type: product.type,
        tags: product.tags,
        featured_image: product.featured_image
      },
      variant: {
        id: variant.id,
        title: variantTitle,
        sku: variant.sku,
        price: variant.price,
        image: variant.featured_image || product.featured_image
      },
      quantity: data.itemCount || 1
    })
  }

  /**
   * Track add to cart
   */
  trackAddToCart(data) {
    const { product, variant, quantity = 1 } = data

    if (this.useShopifyTracking) {
      // Use Shopify's Customer Events API for add to cart
      window.Shopify.analytics.publish('product_added_to_cart', {
        cartLine: {
          quantity: quantity,
          merchandise: {
            id: variant.id,
            price: {
              amount: variant.price / 100,
              currencyCode: CONFIG.DEFAULT_CURRENCY
            },
            sku: variant.sku,
            title: variant.title,
            product: {
              id: product.id,
              title: product.title,
              vendor: product.vendor || CONFIG.DEFAULT_PRODUCT_BRAND,
              type: product.type,
              url: window.location.pathname
            }
          }
        }
      })
    } else {
      // Facebook Pixel
      if (this.isFBPixelLoaded) {
        window.fbq('track', 'AddToCart', {
          content_name: product.title,
          content_ids: [variant.sku || variant.id],
          content_type: 'product',
          value: (variant.price / 100) * quantity,
          currency: CONFIG.DEFAULT_CURRENCY
        })
      }

      // GTM Enhanced Ecommerce
      this.pushToDataLayer({
        event: 'add_to_cart',
        ecommerce: {
          currency: CONFIG.DEFAULT_CURRENCY,
          value: (variant.price / 100) * quantity,
          items: [{
            item_id: variant.sku || variant.id,
            item_name: product.title,
            item_brand: CONFIG.DEFAULT_PRODUCT_BRAND,
            item_category: product.type || CONFIG.DEFAULT_PRODUCT_CATEGORY,
            item_variant: variant.title,
            price: variant.price / 100,
            quantity: quantity
          }]
        }
      })
    }

    // Klaviyo (always track regardless of Shopify integration)
    this.pushToKlaviyo('Added to Cart', {
      $value: (variant.price / 100) * quantity,
      ProductName: product.title,
      ProductID: product.id,
      SKU: variant.sku,
      Categories: product.tags || [],
      ImageURL: variant.image || product.featured_image,
      URL: window.location.href,
      Brand: CONFIG.DEFAULT_PRODUCT_BRAND,
      Price: variant.price / 100,
      Quantity: quantity
    })

    // Only show variant title in console if it's different from product title
    const logMessage = variant.title === product.title
      ? `[Analytics] Add to cart tracked: ${product.title}`
      : `[Analytics] Add to cart tracked: ${product.title} ${variant.title}`
    console.log(logMessage)
  }

  /**
   * Track custom event
   */
  trackEvent(eventData) {
    const { action, category, label, value } = eventData

    this.pushToDataLayer({
      event: 'custom_event',
      eventAction: action,
      eventCategory: category,
      eventLabel: label,
      eventValue: value
    })

    console.log('[Analytics] Custom event tracked:', eventData)
  }

  /**
   * Identify user for Klaviyo
   */
  identifyUser(email, properties = {}) {
    if (!this.isKlaviyoLoaded) {
      window.addEventListener('klaviyo-loaded', () => {
        window._learnq.push(['identify', { $email: email, ...properties }])
      }, { once: true })
      return
    }

    window._learnq.push(['identify', { $email: email, ...properties }])
    console.log('[Analytics] User identified:', email)
  }
}

// Initialize analytics when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.analytics = new Analytics()
    window.analytics.init()
  })
} else {
  window.analytics = new Analytics()
  window.analytics.init()
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Analytics
}
